/**
 * lockService.js - 集計ロック（締め切り）機能のAPI通信
 */

/**
 * 登録済みの全ロック期間（解除済みも含む）を取得する。
 * @returns {Promise<object[]>}
 */
export async function fetchLockPeriods() {
  const res = await fetch('/api/lockstatus');
  if (!res.ok) throw new Error(`ロック期間取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 日付範囲を指定してロックを登録する。
 * 範囲内に未入力の店舗・レジ・日付があれば登録は行われず、エラーに `missing` 配列が付与される。
 * @param {object} params
 * @param {string} params.startDate - 開始日 (YYYYMMDD)
 * @param {string} params.endDate   - 終了日 (YYYYMMDD)
 * @param {string} params.lockedBy  - 操作者名
 * @returns {Promise<void>}
 */
export async function createLockPeriod({ startDate, endDate, lockedBy }) {
  const res = await fetch('/api/lockperiod', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate, lockedBy }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    const error = new Error(json.error || `ロック登録失敗: ${res.status}`);
    error.missing = json.missing ?? null;
    throw error;
  }
}

/**
 * 指定IDのロックを解除する（物理削除はせず解除記録を残す）。
 * @param {number} id
 * @param {string} unlockedBy - 操作者名
 * @returns {Promise<void>}
 */
export async function unlockLockPeriod(id, unlockedBy) {
  const res = await fetch(`/api/lockperiod/${id}/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ unlockedBy }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `ロック解除失敗: ${res.status}`);
  }
}

/**
 * 登録済みの全レコード単位解除（再ロック済みも含む）を取得する。
 * @returns {Promise<object[]>}
 */
export async function fetchRecordUnlocks() {
  const res = await fetch('/api/recordunlock');
  if (!res.ok) throw new Error(`レコード単位解除の取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 店舗・レジ・日付を1件指定してレコード単位の解除を登録する。
 * @param {object} params
 * @param {number} params.shopNo
 * @param {number} params.regiNo
 * @param {string} params.yyyymmdd  - 対象日付 (YYYYMMDD)
 * @param {string} params.unlockedBy - 操作者名
 * @returns {Promise<void>}
 */
export async function createRecordUnlock({ shopNo, regiNo, yyyymmdd, unlockedBy }) {
  const res = await fetch('/api/recordunlock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shopNo, regiNo, yyyymmdd, unlockedBy }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `レコード単位解除の登録失敗: ${res.status}`);
  }
}

/**
 * 有効な（RelockedAt IS NULL の）全 RecordUnlock を一括再ロックする。
 * 締め切り登録完了後に呼び出す。
 * @param {string} person - 操作者名
 * @returns {Promise<void>}
 */
export async function relockAllRecordUnlocks(person) {
  const res = await fetch('/api/recordunlock/relock-all', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ person }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `一括再ロック失敗: ${res.status}`);
  }
}

/**
 * 指定IDのレコード単位解除を取り消す（再ロック。物理削除はせず解除記録を残す）。
 * @param {number} id
 * @param {string} relockedBy - 操作者名
 * @returns {Promise<void>}
 */
export async function relockRecordUnlock(id, relockedBy) {
  const res = await fetch(`/api/recordunlock/${id}/relock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ relockedBy }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `再ロック失敗: ${res.status}`);
  }
}
